import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { LANGUAGES, DEFAULT_LANG, STORAGE_KEY, setLocale } from "@/i18n/languages";
import { translator } from "@/i18n/autoTranslate";

const I18nCtx = createContext({ lang: DEFAULT_LANG, t: (s) => s, setLang: () => {}, busy: false });

const initial = () => {
  const s = localStorage.getItem(STORAGE_KEY);
  return LANGUAGES.some((l) => l.code === s) ? s : DEFAULT_LANG;
};

const interpolate = (s, vars) => (vars ? s.replace(/\{(\w+)\}/g, (m, k) => (vars[k] ?? m)) : s);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(initial);
  const [catalog, setCatalog] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLocale(lang);
    document.documentElement.lang = lang;
    if (lang === DEFAULT_LANG) { setCatalog({}); return; }
    import(`./locales/${lang}.json`).then((m) => setCatalog(m.default || m)).catch(() => setCatalog({}));
  }, [lang]);

  useEffect(() => { translator.setLanguage(lang, catalog, setBusy); }, [lang, catalog]);

  const setLang = useCallback((c) => { localStorage.setItem(STORAGE_KEY, c); setLangState(c); }, []);

  const t = useCallback((s, vars) => {
    const e = catalog[s];
    let out = s;
    if (typeof e === "string") out = e;
    else if (e && vars?.count != null) out = e[new Intl.PluralRules(lang).select(vars.count)] || e.other || s;
    return interpolate(out, vars);
  }, [catalog, lang]);

  const value = useMemo(() => ({ lang, setLang, t, busy }), [lang, setLang, t, busy]);
  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export const useI18n = () => useContext(I18nCtx);
